import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, User, Lock } from "lucide-react";

const authBaseUrl = `${import.meta.env.VITE_BLOCKS_API_URL?.replace(/\/$/, "") || "https://blocksapi.slsblx.com"}/iam/v4`;
const tenantId = import.meta.env.VITE_X_BLOCKS_KEY || "";

function ActivateCard({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white rounded-2xl shadow-xl ring-1 ring-black/5 p-8 w-full max-w-md">
        <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-xl shadow-md">
          SE
        </div>
        {children}
      </div>
    </div>
  );
}

export default function Activate() {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const code = searchParams.get("code") || "";

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [error, setError] = useState("");

  const isLoading = status === "loading";
  const isSuccess = status === "success";

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setStatus("loading");

    try {
      const response = await fetch(`${authBaseUrl}/auth/activate`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-blocks-key": tenantId,
        },
        body: JSON.stringify({ code, password, firstName, lastName }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || "Activation failed. Please check your code and try again.");
      }

      setStatus("success");
      setTimeout(() => navigate("/", { replace: true }), 3000);
    } catch (err) {
      setError(err.message || "Network error. Please try again.");
      setStatus("error");
    }
  }

  if (!code) {
    return (
      <ActivateCard>
        <p role="alert" className="text-center text-sm text-red-600">
          Invalid activation link. Please check your email for the correct link.
        </p>
      </ActivateCard>
    );
  }

  if (isSuccess) {
    return (
      <ActivateCard>
        <p role="status" className="text-center text-sm text-gray-700">
          Account activated! Redirecting to login…
        </p>
      </ActivateCard>
    );
  }

  return (
    <ActivateCard>
      <h1 className="text-xl font-bold text-gray-900 text-center">Set your password</h1>
      <p className="text-sm text-gray-500 mt-1.5 mb-6 text-center">
        Finish setting up your SELISE Group account
      </p>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-600 text-sm" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">First name</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <Input
              id="firstName"
              name="firstName"
              type="text"
              autoComplete="given-name"
              autoFocus
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={isLoading}
              className="pl-10 h-12"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName">Last name</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <Input
              id="lastName"
              name="lastName"
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={isLoading}
              className="pl-10 h-12"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="new-password"
              minLength={8}
              placeholder="At least 8 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              className="pl-10 h-12"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" aria-hidden="true" />
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              minLength={8}
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              className="pl-10 h-12"
              required
            />
          </div>
        </div>

        <Button type="submit" className="w-full h-12 font-medium" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Activating…
            </>
          ) : (
            "Activate account"
          )}
        </Button>
      </form>
    </ActivateCard>
  );
}
